// hooks/useFormatTemplates.ts
import { useState, useEffect, useCallback } from "react";
import { logger } from "@/helpers/tslog";
import useDebounce from "@/utils/useDebounce";
import { FormatTemplate } from "@/utils/entities";
import { httpClient } from "../api/client";

interface UseFormatTemplatesReturn {
  formatTemplates: FormatTemplate[];
  searchQuery: string;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  resetSearch: () => void;
  fetchTemplates: (reset?: boolean) => Promise<void>;
  handlePrimaryTemplate: (data: SetPrimaryFormatParams, fetchCallback: any, fetchRecentlyUsedCallBack: any) => void;
}

interface SetPrimaryFormatParams {
  formatId: string;
  orgId: string;
  isPrimary: boolean;
}

const useFormatTemplates = (limit: number = 3): UseFormatTemplatesReturn => {
  const [formatTemplates, setFormatTemplates] = useState<FormatTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const fetchTemplates = useCallback(
    async (query: string = "", pageNum: number = 1, reset: boolean = false) => {
      if (isLoading) return;

      setIsLoading(true);

      try {
        const response = await httpClient.get("/format-template", {
          params: {
            search: query,
            page: pageNum,
            limit,
          },
        });

        const newTemplates = response.data;

        if (pageNum === 1 || reset) {
          setFormatTemplates(newTemplates.data);
          setPage(2);
        } else {
          setFormatTemplates((prev) => [...prev, ...newTemplates.data]);
          setPage((prev) => prev + 1);
        }

        setHasMore(newTemplates.pagination.hasMore);
      } catch (e: any) {
        logger.error("Failed to fetch templates:", e);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, limit]
  );

  const debouncedSearch = useDebounce(searchQuery, 500);

  useEffect(() => {
    fetchTemplates(debouncedSearch, 1, true);
  }, [debouncedSearch]);

  const loadMore = useCallback(async () => {
    if (!isLoading && hasMore) {
      setIsLoading(true);
      await fetchTemplates(searchQuery, page);
    }
  }, [searchQuery, page, isLoading, hasMore, fetchTemplates]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handlePrimaryTemplate = async (
    { formatId, orgId, isPrimary }: SetPrimaryFormatParams,
    fetchCallback: any,
    fetchTemplatesRecentlyUsed: any
  ) => {
    if (isPrimary) return;

    try {
      await httpClient.post("/format/primary-format", {
        formatId,
        orgId,
      });

      fetchCallback();
      fetchTemplatesRecentlyUsed();
    } catch (error: any) {
      console.error("Failed to set primary format:", error?.response?.data || error.message);
      throw error;
    }
  };

  const resetSearch = useCallback(async () => {
    setSearchQuery("");
    await fetchTemplates("", 1, true);
  }, [fetchTemplates]);

  return {
    formatTemplates,
    searchQuery,
    isLoading,
    hasMore,
    loadMore,
    handleSearchChange,
    resetSearch,
    fetchTemplates: () => fetchTemplates(searchQuery, 1, true),
    handlePrimaryTemplate,
  };
};

export default useFormatTemplates;